import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, useTheme, Dialog, DialogContent, Paper } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import FooterSection from "../../../shared/sections/FooterSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import FormulationTable from "../../../shared/modules/FormulationTable";
// import CookiesResultsSection from "./results/CookiesResultsSection";
import CookiesResultsSection from "../../../shared/sections/ResultsSection";
import ProgressiveAnalysisTracker from "./ProgressiveAnalysisTracker";
import { CookiesQuestions } from "./questions";
import { CookiesPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { theme } from "../../../design/theme";
import { apiFetch } from "../../../api/apiClient";

const { perlColors, perlTypography } = theme.custom;

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "packaging", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({ assumptions = {} }) {
  return (
    <Box>
      {assumptions && Object.keys(assumptions).length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "18px",
              fontFamily: perlTypography.family,
              letterSpacing: "0.3px",
              color: perlColors.textPrimary,
            }}
          >
            Assumptions
          </Typography>

          {/* Render each subsection if available */}
          {assumptions.Formulation && assumptions.Formulation.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Formulation
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Formulation.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Process && assumptions.Process.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Process
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Process.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.Packaging && assumptions.Packaging.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                Packaging
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.Packaging.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {assumptions.OHP && assumptions.OHP.length > 0 && (
            <Box sx={{ mb: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  fontSize: "13px",
                  fontWeight: 600,
                  fontFamily: perlTypography.family,
                  color: perlColors.textPrimary,
                  letterSpacing: "0.3px",
                }}
              >
                OHP
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {assumptions.OHP.map((assumption, idx) => (
                  <Typography
                    key={idx}
                    component="li"
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "14px",
                      fontFamily: perlTypography.family,
                      color: perlColors.textSecondary,
                      lineHeight: "20px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {assumption}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default function CookiesPage() {
  const [searchParams] = useSearchParams();
  const commodity = searchParams.get("commodity") || "Cookies";

  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [assumptionsData, setAssumptionsData] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Track which section was last modified
  const [lastModifiedSection, setLastModifiedSection] = useState(null);

  // Functional groups for formulation
  const functionalGroups = [
    "Bulk ingredient",
    "Sweetners",
    "Oil and fats",
    "Active ingredient",
    "Leavening agents",
    "Flavour enhancers",
    "Flavours",
    "Colours",
    "Preservatives",
    "Emulsifiers",
    "Stabilizers",
    "Thickeners",
    "Acidulants",
    "Vitamins and minerals",
    "Other",
  ];
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
    setLastModifiedSection("product");
  };

  const handlePlayBookChange = (field, value) => {
    setPlayBookData({ ...playBookData, [field]: value });
    setLastModifiedSection("playbook");
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("≡ƒÜÇ Analyzing cost, risk, and substitutes with:", {
        recipe,
        playbook,
        product,
      });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("Γ£à Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("Γ£à Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("ΓÜá∩╕Å Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("Γ£à Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("ΓÜá∩╕Å Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));
      setLastModifiedSection("results"); // Mark as results to prevent re-triggering

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        tower: "FB",
        commodity: "Cookies",
        productName: product.productName,
        companyName: "Kearney",
        brandName: "PERLab",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("≡ƒû╝∩╕Å Generating product image:", imagePayload);

      const response = await apiFetch("/api/ai/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("Γ£à Product image generated:", data.image_url);
    } catch (error) {
      console.error("Γ¥î Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const handleQuickCost = async () => {
    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);

    try {
      // Call F&B V1 API - Single comprehensive AI call with full schema
      console.log("≡ƒÜÇ Calling /api/fb/cost-analysis with:", productData);

      const response = await apiFetch("/api/fb/cost-analysis", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          commodity: "COOKIES",
          operation: "unified_quick_cost",
          productName: productData.productName,
          marketPositioning: productData.marketPositioning,
          annualVolume: productData.annualVolume,
          manufacturingCountry: productData.manufacturingCountry,
          skuWeight: productData.skuWeight,
          skuWeightUnit: productData.skuWeightUnit,
        }),
      });

      // V2 ENDPOINT (commented - uses progressive generation):
      // const response = await apiFetch("/api/fb/v2/cost-analysis-progressive", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({
      //     productName: productData.productName,
      //     marketPositioning: productData.marketPositioning,
      //     annualVolume: productData.annualVolume,
      //     country: productData.manufacturingCountry,
      //     skuWeight: productData.skuWeight,
      //     skuWeightUnit: productData.skuWeightUnit,
      //   }),
      // });

      if (!response.ok) {
        let errorMessage = "Failed to generate quick cost";
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          if (errorData.missing_fields) {
            console.error("Missing fields:", errorData.missing_fields);
          }
        } catch (e) {
          console.error("Error parsing error response:", e);
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("Γ£à Cost Analysis complete:", result);

      // Extract results from backend response
      const playbook = result.Playbook_Data || null;
      const shouldCostResult = result.ShouldCost_Result || null;
      const recipeData = result.Recipe_Data || [];
      const riskData = result.Risk_Data || null;
      const substituteData = result.Substitute_Data || null;

      // Debug: Log risk and substitute data structure
      console.log("≡ƒöì Risk Data:", riskData);
      console.log("≡ƒöì Substitute Data:", substituteData);
      console.log("≡ƒÆ░ Should Cost Result:", shouldCostResult);
      console.log("≡ƒº¬ Recipe Data:", recipeData);

      // Transform backend data to match PerlCostTable format
      const costData = shouldCostResult
        ? {
            breakdown: {
              material: shouldCostResult.MaterialCost || 0,
              process: shouldCostResult.ProcessCost || 0,
              packaging: shouldCostResult.PackagingCost || 0,
              ohp: shouldCostResult.OHPCost || 0,
            },
          }
        : null;

      // Extract database calculation results
      const calculationResult = result.calculationResult || {};
      console.log("≡ƒôè Database calculation results:", calculationResult);

      // Ensure playbook has packaging defaults if missing
      if (
        playbook &&
        (!playbook.primary_packaging ||
          !playbook.secondary_packaging ||
          !playbook.tertiary_packaging)
      ) {
        console.warn("ΓÜá∩╕Å Packaging fields missing in playbook, applying defaults");
        playbook.primary_packaging = playbook.primary_packaging || "Flow Wrap";
        playbook.secondary_packaging = playbook.secondary_packaging || "Carton Box";
        playbook.tertiary_packaging = playbook.tertiary_packaging || "Pallet";
      }

      // Update state with results
      setPlayBookData(playbook);
      setRecipeData(recipeData);
      setCostData(costData);
      setRiskData(riskData);
      setSubstituteData(substituteData);

      // TODO: Merge calculation results with cost data
      // You can enhance costData with calculated values from database
      // For now, we're keeping AI results as primary display

      // Generate product image
      if (playbook) {
        await generateProductImageWithData(productData, playbook);
      }

      // Navigate to results and mark all sections as started
      setActiveSection("results");
      setSectionStarted((prev) => ({
        ...prev,
        formulation: true,
        playbook: true,
        results: true,
      }));
      setLastModifiedSection("results");

      console.log("Γ£à Quick Cost completed, data ready for analysis");
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to generate quick cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const getTimestampId = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };

  /**
   * NEW PROGRESSIVE QUICK COST HANDLER (V2)
   *
   * This is the new micro-schema approach with step-by-step execution.
   * Each step calls a separate API endpoint with focused schema.
   *
   * Benefits:
   * - Progress visibility for users
   * - Better error handling (retry individual steps)
   * - Independent schema versioning
   * - Parallel execution where possible
   * - Partial results on failure
   *
   * Steps:
   * 1. Generate Formulation      ΓåÆ /api/fb/v2/generate-formulation
   * 2. Generate General Info     ΓåÆ /api/fb/v2/generate-general-info
   * 3. Generate Goods Return     ΓåÆ /api/fb/v2/generate-goods-return
   * 4. Generate Material         ΓåÆ /api/fb/v2/generate-material
   * 5. Generate Packaging        ΓåÆ /api/fb/v2/generate-packaging
   * 6. Generate PlayBook         ΓåÆ /api/fb/v2/generate-playbook
   * 7. Analyze Risks            ΓåÆ /api/fb/v2/analyze-risks
   * 8. Find Substitutes         ΓåÆ /api/fb/v2/find-substitutes
   */
  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("≡ƒÜÇ Starting Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps immediately
    const totalSteps = 11;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);

    // Track maximum progress to prevent going backwards
    let maxProgress = 0;

    try {
      const timestamp = getTimestampId();

      // Prepare request payload
      const requestData = {
        productName: productData.productName || "Premium Chocolate Chip Cookie",
        country: productData.manufacturingCountry || "USA",
        skuWeight: productData.skuWeight || 50,
        skuWeightUnit: productData.skuWeightUnit || "g",
        marketPositioning: productData.marketPositioning || "Premium",
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // partKey: `PART_${timestamp}`,
        // projectId: `PROJ_1764775036995`,
        // partKey: `PART_1764775036995`,
        annualVolume: productData.annualVolume || 100000,
      };

      console.log("≡ƒôñ Calling streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/fb/cookies/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("≡ƒô¿ SSE Event:", eventData);

            if (eventData.type === "start") {
              // Update total steps if different from initial estimate
              const totalSteps = eventData.total_steps || 10;
              if (totalSteps !== progressSteps.length) {
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              // Update specific step status
              const stepIndex = eventData.step - 1;
              console.log(
                `≡ƒôè Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`
              );

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  // Map backend status to frontend status
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: eventData.label || updated[stepIndex].label,
                    status: mappedStatus,
                    message:
                      eventData.message ||
                      (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };

                  console.log(
                    `   Γ£à Updated step ${eventData.step}: label="${updated[stepIndex].label}", status="${updated[stepIndex].status}"`
                  );
                }
                return updated;
              });

              // Update current step based on the most recent event
              // For parallel execution, show the step that just changed status
              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              // Analysis complete - populate all state from final response
              console.log("Γ£à Analysis complete:", eventData);
              console.log("≡ƒöì Complete event keys:", Object.keys(eventData));
              console.log("≡ƒöì Has playBookData?", !!eventData.playBookData);
              console.log("≡ƒöì Has riskData?", !!eventData.riskData);
              console.log("≡ƒöì Has substituteData?", !!eventData.substituteData);
              console.log("≡ƒöì Has productImage?", !!eventData.productImage);
              if (eventData.productImage) {
                console.log("≡ƒû╝∩╕Å Product image data:", eventData.productImage);
              }

              // Set progress to 100%
              maxProgress = 100;
              setOverallProgress(100);

              // Mark all steps as completed
              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                }))
              );

              // Populate state from complete event data
              if (eventData.playBookData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedPlayBookData =
                  eventData.playBookData.playBookData || eventData.playBookData;
                console.log("≡ƒôï Setting playbook data:", unwrappedPlayBookData);
                console.log("≡ƒöì Playbook data keys:", Object.keys(unwrappedPlayBookData));
                console.log("≡ƒöì Has primary_packaging?", unwrappedPlayBookData.primary_packaging);
                console.log(
                  "≡ƒöì Has secondary_packaging?",
                  unwrappedPlayBookData.secondary_packaging
                );
                console.log(
                  "≡ƒöì Has tertiary_packaging?",
                  unwrappedPlayBookData.tertiary_packaging
                );
                setPlayBookData(unwrappedPlayBookData);
              }

              if (eventData.riskData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedRiskData = eventData.riskData.riskData || eventData.riskData;
                console.log("ΓÜá∩╕Å Setting risk data:", unwrappedRiskData);
                console.log("≡ƒöì Risk data structure:", {
                  hasMatrix: !!unwrappedRiskData.matrix,
                  hasSummary: !!unwrappedRiskData.summary,
                  hasRiskItems: !!unwrappedRiskData.risk_items,
                  keys: Object.keys(unwrappedRiskData),
                });
                setRiskData(unwrappedRiskData);
              }

              if (eventData.substituteData) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedSubstituteData =
                  eventData.substituteData.substituteData || eventData.substituteData;
                console.log("≡ƒöä Setting substitute data:", unwrappedSubstituteData);
                console.log("≡ƒöì Substitute data structure:", {
                  hasSummary: !!unwrappedSubstituteData.summary,
                  hasIdeas: !!unwrappedSubstituteData.ideas,
                  keys: Object.keys(unwrappedSubstituteData),
                });
                setSubstituteData(unwrappedSubstituteData);
              }

              if (eventData.productImage?.imageUrl) {
                console.log("≡ƒû╝∩╕Å Setting product image:", eventData.productImage.imageUrl);
                setProductImageUrl(eventData.productImage.imageUrl);
              } else {
                console.log("ΓÜá∩╕Å No product image URL in response");
              }

              if (eventData.ShouldCostResult) {
                console.log("≡ƒÆ░ Setting cost data");
                const costData = {
                  breakdown: {
                    material: eventData.ShouldCostResult.MaterialCost || 0,
                    process: eventData.ShouldCostResult.ProcessCost || 0,
                    packaging: eventData.ShouldCostResult.PackagingCost || 0,
                    ohp: eventData.ShouldCostResult.OHPCost || 0,
                  },
                  total: eventData.ShouldCostResult.TotalCost || 0, // Pass the correct total from DB
                };
                setCostData(costData);
              }

              if (eventData.RecipeData) {
                console.log("≡ƒº¬ Setting recipe data");
                setRecipeData(eventData.RecipeData);
              }

              if (eventData.assumptions) {
                console.log("📝 Setting assumptions data:", eventData.assumptions);
                console.log("   Assumptions sections:", Object.keys(eventData.assumptions));
                setAssumptionsData(eventData.assumptions);
              }

              if (eventData.KeyInsightsJson) {
                // Backend wraps data in extra layer, unwrap it
                const unwrappedKeyInsights =
                  eventData.KeyInsightsJson.key_insights || eventData.KeyInsightsJson;
                console.log("🔑 Setting key insights data:", unwrappedKeyInsights);
                console.log("   Key insights keys:", Object.keys(unwrappedKeyInsights));
                console.log("   Has cost_savings?", !!unwrappedKeyInsights.cost_savings);
                console.log("   Has strategic?", !!unwrappedKeyInsights.strategic);
                setKeyInsightsData(unwrappedKeyInsights);
              }

              // Navigate to results after short delay
              setTimeout(() => {
                setShowProgressTracker(false);
                setIsProcessing(false);
                setActiveSection("results");
                setSectionStarted((prev) => ({
                  ...prev,
                  formulation: true,
                  playbook: true,
                  results: true,
                }));
                setLastModifiedSection("results");
              }, 2000);

              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.error || eventData.message || "Analysis failed");
            }
          } catch (parseError) {
            console.error("Γ¥î Error parsing SSE event:", parseError);
          }
        }
      }

      console.log("Γ£à Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("Γ¥î Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with formulation, manufacturing parameters, and detailed insights.",
    },
    {
      id: "formulation",
      label: "Formulation",
      icon: <ScienceIcon />,
      disabled: false,
      title: "Ready to Build Formulation",
      description: "View and edit ingredient composition with functional groups and percentages.",
    },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: false,
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: false,
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);

  const costTotal = useMemo(() => {
    const declaredTotal = costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows);
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="F&B"
          industry="Food & Beverage"
          commodity={commodity}
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height: "92%" }}>
                <CookiesQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                  commodity={commodity}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                    setLastModifiedSection("formulation");
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                    setLastModifiedSection("formulation");
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                    setLastModifiedSection("formulation");
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <CookiesPlayBook
                    data={playBookData}
                    onChange={handlePlayBookChange}
                    tower="FB"
                    commodity={commodity}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <>
                    <CookiesResultsSection
                      costData={costData}
                      costRows={costRows}
                      costTotal={costTotal}
                      riskData={riskData}
                      substituteData={substituteData}
                      productImageUrl={productImageUrl}
                      generatingImage={generatingImage}
                      onRegenerateImage={generateProductImage}
                      productName={productData?.productName}
                      productData={productData}
                      brandColor="#ff6034"
                      assumptions={assumptionsData || {}}
                      keyInsights={keyInsightsData || null}
                      onNavigateToPlayBook={() => {
                        setSectionStarted((prev) => ({ ...prev, playbook: true }));
                        setActiveSection("playbook");
                      }}
                      renderedAssumptions={renderAssumptions({
                        assumptions: assumptionsData || {},
                      })}
                    />

                    {/* JSON Viewer for Raw Response */}
                    {/* <Box sx={{ mt: 4, mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: perlColors.textPrimary,
                          mb: 2,
                          fontWeight: 600,
                        }}
                      >
                        Raw API Response
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: "#1e1e1e",
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <ReactJson
                          src={{
                            recipe: recipeData,
                            playBookData: playBookData,
                            costData: costData,
                            riskData: riskData,
                            substituteData: substituteData,
                          }}
                          theme="monokai"
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={true}
                          enableClipboard={true}
                          style={{
                            backgroundColor: "transparent",
                            fontSize: "14px",
                            fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                          }}
                        />
                      </Box>
                    </Box> */}
                  </>
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button - calls progressive V2 handler */}
        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleProgressiveQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
